import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { workerService } from '../services/workerService';
import Input from '../components/ui/Input';
import Button from '../components/ui/Button';
import FileUpload from '../components/ui/FileUpload';

export default function WorkerFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    username: '',
    password: '',
    role: '',
    image: null,
  });
  const [existingImage, setExistingImage] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isEdit) {
      fetchWorker();
    }
  }, [id]);

  const fetchWorker = async () => {
    try {
      const worker = await workerService.getById(id);
      setFormData({
        first_name: worker.first_name || '',
        last_name: worker.last_name || '',
        email: worker.email || '',
        username: worker.username || '',
        password: '',
        role: worker.extra_role || '',
        image: null,
      });
      setExistingImage(worker.image_url || '');
    } catch (error) {
      console.error('Error fetching worker:', error);
    } finally {
      setFetching(false);
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.first_name.trim()) newErrors.first_name = 'Имя обязательно';
    if (!formData.last_name.trim()) newErrors.last_name = 'Фамилия обязательна';
    if (!formData.email.trim()) newErrors.email = 'Email обязателен';
    if (!formData.username.trim()) newErrors.username = 'Имя пользователя обязательно';
    if (!isEdit && !formData.password) newErrors.password = 'Пароль обязателен';
    if (!formData.role.trim()) newErrors.role = 'Должность обязательна';
    if (!isEdit && !formData.image) newErrors.image = 'Загрузите фото';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      if (isEdit) {
        await workerService.update(id, {
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          username: formData.username,
        });
      } else {
        const data = new FormData();
        data.append('first_name', formData.first_name);
        data.append('last_name', formData.last_name);
        data.append('email', formData.email);
        data.append('username', formData.username);
        data.append('password', formData.password);
        data.append('role', formData.role);
        if (formData.image) {
          data.append('image', formData.image);
        }
        await workerService.create(data);
      }
      navigate('/admin/workers');
    } catch (error) {
      console.error('Error saving worker:', error);
      setErrors({ submit: 'Ошибка сохранения. Попробуйте ещё раз.' });
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <button
          onClick={() => navigate('/admin/workers')}
          className="flex items-center text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Назад к сотрудникам
        </button>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">
          {isEdit ? 'Редактирование сотрудника' : 'Добавление сотрудника'}
        </h1>

        {errors.submit && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
            {errors.submit}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label="Имя"
              value={formData.first_name}
              onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
              error={errors.first_name}
              placeholder="Введите имя"
            />

            <Input
              label="Фамилия"
              value={formData.last_name}
              onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
              error={errors.last_name}
              placeholder="Введите фамилию"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              error={errors.email}
              placeholder="email@example.com"
            />

            <Input
              label="Имя пользователя"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              error={errors.username}
              placeholder="username"
            />
          </div>

          {!isEdit && (
            <Input
              label="Пароль"
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              error={errors.password}
              placeholder="********"
            />
          )}

          <Input
            label="Должность"
            value={formData.role}
            onChange={(e) => setFormData({ ...formData, role: e.target.value })}
            error={errors.role}
            placeholder="Например: Директор, Менеджер"
            disabled={isEdit}
          />

          {!isEdit && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Фото <span className="text-red-500">*</span>
              </label>
              {existingImage && (
                <div className="mb-4">
                  <p className="text-sm text-gray-500 mb-2">Текущее фото:</p>
                  <img
                    src={existingImage}
                    alt="Current photo"
                    className="h-32 w-32 rounded-full object-cover"
                  />
                </div>
              )}
              <FileUpload
                onChange={(file) => setFormData({ ...formData, image: file })}
                accept="image/*"
                error={errors.image}
              />
            </div>
          )}

          {isEdit && existingImage && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Текущее фото
              </label>
              <img
                src={existingImage}
                alt="Current photo"
                className="h-32 w-32 rounded-full object-cover"
              />
              <p className="text-sm text-gray-500 mt-2">
                Изменение фото через API пока не поддерживается
              </p>
            </div>
          )}

          <div className="flex justify-end gap-4">
            <Button type="button" variant="secondary" onClick={() => navigate('/admin/workers')}>
              Отмена
            </Button>
            <Button type="submit" loading={loading}>
              {isEdit ? 'Сохранить' : 'Добавить'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
